import { getMapStyle } from "../../../utils/getMapStyle";
import { UserService } from "../../../services/UserService";
import { userOptionsKeys } from "../../../hooks/queries/useUserOptions";
import { queryClient } from "../../../queryClient";
import { translate } from "../../../utils/translationUtils";

const MAP_ICON = `
  <svg viewBox="0 0 24 24" width="18" height="18" style="display: block; margin: auto;">
    <polygon points="3,5 9,3 15,5 21,3 21,19 15,21 9,19 3,21" fill="currentColor" opacity="0.15" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
    <line x1="9" y1="3" x2="9" y2="19" stroke="currentColor" stroke-width="1.2"/>
    <line x1="15" y1="5" x2="15" y2="21" stroke="currentColor" stroke-width="1.2"/>
  </svg>
`;

const SATELLITE_ICON = `
  <svg viewBox="0 0 24 24" width="18" height="18" style="display: block; margin: auto;">
    <circle cx="12" cy="12" r="4.5" fill="currentColor"/>
    <ellipse cx="12" cy="12" rx="10" ry="4" fill="none" stroke="currentColor" stroke-width="1.4" transform="rotate(-25 12 12)"/>
  </svg>
`;

/**
 * Custom Mapbox control that toggles the map style between "roadmap" and
 * "satellite", mirroring (and persisting to) the "Map Style" preference on
 * the account page so the choice stays in sync everywhere.
 */
export default class MapStyleToggleControl {
  constructor({ roadmapStyle, themeMode = "light" } = {}) {
    this._roadmapStyle = roadmapStyle;
    this._themeMode = themeMode;
  }

  onAdd(map) {
    this._map = map;

    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

    this._btn = document.createElement("button");
    this._btn.className = "mapboxgl-ctrl-icon mapboxgl-ctrl-map-style-toggle";
    this._btn.type = "button";
    this._btn.onclick = () => this._toggle();

    this._render();

    this._container.appendChild(this._btn);
    return this._container;
  }

  onRemove() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    this._map = undefined;
  }

  // Called when the surrounding theme (light/dark) changes so the roadmap
  // style stays correct without recreating the control.
  setThemeMode(themeMode) {
    if (this._themeMode === themeMode) return;
    this._themeMode = themeMode;

    if (this._map && this._getPreference() !== "satellite") {
      this._map.setStyle(getMapStyle(this._themeMode, this._roadmapStyle));
    }
  }

  _getPreference() {
    return localStorage.getItem("map_style") || "roadmap";
  }

  _render() {
    const isSatellite = this._getPreference() === "satellite";
    this._btn.innerHTML = isSatellite ? MAP_ICON : SATELLITE_ICON;
    const title = isSatellite
      ? translate("common.switch_to_map_view")
      : translate("common.switch_to_satellite_view");
    this._btn.title = title;
    this._btn["aria-label"] = title;
  }

  _toggle() {
    const next =
      this._getPreference() === "satellite" ? "roadmap" : "satellite";
    localStorage.setItem("map_style", next);
    this._render();

    if (this._map) {
      this._map.setStyle(getMapStyle(this._themeMode, this._roadmapStyle));
    }

    // Persist globally, same as the account page preference, and keep the
    // cached user options in sync so the account page reflects the change.
    UserService.updateUserOptions({ map_style: next })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: userOptionsKeys.options() });
      })
      .catch(() => {});
  }
}
